import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Search, Trash2 } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { adminService } from '../../services/adminService';
import { metricsService } from '../../services/metricsService';
import { billingService } from '../../services/billingService';
import { indexByEmail, formatShortDate, daysLeft, expiryTone } from '../../lib/subscription';
import {
  ROLES, ROLE_STYLES, QUEUE_CLASSES, QUEUE_CLASS_LABELS, QUEUE_CLASS_ICONS, defaultQueueClass,
} from '../../lib/roles';
import EditUserDialog from './EditUserDialog';
import CreateServiceTokenDialog from './CreateServiceTokenDialog';

const PAGE_SIZE = 20;
// Подписки тянем одним запросом и раскладываем по email: bit-billing фильтрует
// только по одному email за раз, а по строке страницы это 20 запросов.
const SUBS_FETCH_LIMIT = 100;

function toUtcDate(dateStr) {
  const utc = /[zZ]|[+-]\d\d:?\d\d$/.test(dateStr) ? dateStr : dateStr + 'Z';
  return new Date(utc);
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return toUtcDate(dateStr).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function relativeTime(dateStr) {
  if (!dateStr) return '—';
  const diff = Math.floor((new Date() - new Date(dateStr)) / (1000 * 60 * 60 * 24));
  if (diff === 0) return 'сегодня';
  if (diff === 1) return 'вчера';
  if (diff < 7) return `${diff} дн. назад`;
  if (diff < 30) return `${Math.floor(diff / 7)} нед. назад`;
  return '30+ дней';
}

function SortArrow({ field, sortBy, sortOrder }) {
  if (sortBy !== field) return <span className="text-paper-hint ml-1">↕</span>;
  return <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>;
}

function Pagination({ page, totalCount, pageSize, onPageChange, queueClassFilterActive, visibleCount, pageUsersCount }) {
  if (totalCount === 0) return null;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalCount);

  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - 1 && i <= page + 1)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== '...') {
      pages.push('...');
    }
  }

  return (
    <div className="flex justify-between items-center mt-3 text-sm text-paper-ink-3">
      {/* Класс — клиентский фильтр (см. queueClassFilter выше): totalCount он не меняет,
          поэтому «X-Y из totalCount» с активным фильтром был бы неправдой — показываем,
          сколько реально видно на этой странице. */}
      <span>
        {queueClassFilterActive
          ? `Класс сужает только эту страницу: показано ${visibleCount} из ${pageUsersCount} (всего в базе ${totalCount}, без учёта класса)`
          : `Показано ${from}-${to} из ${totalCount}`}
      </span>
      <div className="flex gap-1">
        <button
          className="px-2 py-1 border rounded text-xs disabled:opacity-40"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >←</button>
        {pages.map((p, i) =>
          p === '...' ? (
            <span key={`dots-${i}`} className="px-2 py-1 text-xs">...</span>
          ) : (
            <button
              key={p}
              className={`px-2 py-1 border rounded text-xs ${p === page ? 'bg-paper-ink text-white' : ''}`}
              onClick={() => onPageChange(p)}
            >{p}</button>
          )
        )}
        <button
          className="px-2 py-1 border rounded text-xs disabled:opacity-40"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >→</button>
      </div>
    </div>
  );
}

export default function UsersTab({ onCandidateCount }) {
  const [users, setUsers] = useState([]);
  const [userOffices, setUserOffices] = useState({}); // email_lower -> office (из bit-metrics)
  const [offices, setOffices] = useState([]);         // канон-список для формы создания
  const [userPeople, setUserPeople] = useState({}); // email_lower -> {name, department, job_title}
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  // Класса нет в фильтрах бэкенда (`GET /admin/users` не принимает такой параметр) —
  // фильтруем то, что уже приехало на текущую страницу.
  const [queueClassFilter, setQueueClassFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editUser, setEditUser] = useState(null);
  const [showCreateService, setShowCreateService] = useState(false);
  const [toggleError, setToggleError] = useState(null);

  const [offState, setOffState] = useState({}); // email_lower -> {status, first_flagged_at, deactivated_at}
  const [chip, setChip] = useState('all');      // all | candidate | deactivated | no_office | no_owner | paying
  const [subs, setSubs] = useState({});         // email_lower -> активная подписка
  const [subsTruncated, setSubsTruncated] = useState(0); // сколько подписок не влезло в один запрос

  const debounceRef = useRef(null);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { page, page_size: PAGE_SIZE, sort_by: sortBy, sort_order: sortOrder };
      if (search.trim()) params.search = search.trim();  // без trim ведущий/хвостовой пробел ломал поиск (бэкенд матчит буквально)
      if (roleFilter) params.role = roleFilter;
      // Чип считает бэкенд по всей базе: клиентский фильтр показывал бы только
      // тех, кто попал на текущую страницу (≤20 строк).
      if (chip !== 'all') params.filter = chip;
      const data = await adminService.getUsers(params);
      setUsers(data.users);
      setTotalCount(data.total_count);
      // Админка-юзеры кормится bit-auth (офиса там нет) — дозапрашиваем офис
      // из bit-metrics по email'ам страницы (office_map + org-directory).
      const emails = (data.users || []).map((u) => u.email).filter(Boolean);
      if (emails.length) {
        metricsService.resolveEnrichment(emails)
          .then(({ offices, people }) => { setUserOffices(offices); setUserPeople(people); })
          .catch(() => { setUserOffices({}); setUserPeople({}); });
      } else {
        setUserOffices({});
        setUserPeople({});
      }
    } catch (e) {
      if (e.response?.status === 403) {
        setError('Доступ запрещён');
      } else {
        setError(e.response?.data?.detail || 'Не удалось загрузить пользователей');
      }
    } finally {
      setLoading(false);
    }
  }, [page, search, roleFilter, sortBy, sortOrder, chip]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Канон-список офисов для формы создания — источник тот же, что у вкладки «Офисы».
  useEffect(() => {
    metricsService.getOfficeMap()
      .then((data) => setOffices(data.offices || []))
      .catch(() => setOffices([]));
  }, []);

  const loadOffboarding = useCallback(async () => {
    try {
      const items = await adminService.getOffboardingState();
      const map = {};
      items.forEach((d) => { map[(d.email || '').toLowerCase()] = d; });
      setOffState(map);
      const cand = items.filter((d) => d.status === 'candidate').length;
      onCandidateCount && onCandidateCount(cand);
    } catch { /* оффбординг мог быть выключен — не критично */ }
  }, [onCandidateCount]);

  useEffect(() => { loadOffboarding(); }, [loadOffboarding]);

  // Подписки клиентов для колонки «Тариф». Сбой не критичен — колонка пустая,
  // остальная админка работает. А вот срез по лимиту молчать не должен:
  // «—» у клиента с тарифом читается как «тарифа нет».
  const loadSubs = useCallback(async () => {
    try {
      const data = await billingService.adminGetSubscriptions({ page_size: SUBS_FETCH_LIMIT });
      const items = data.items || [];
      setSubs(indexByEmail(items));
      // Считаем по НЕзагруженным документам, а не по total_count: список без
      // фильтра отдаёт и expired, и canceled, поэтому «130 всего при 40 живых»
      // не значит, что что-то потерялось. Тревожимся только когда страница
      // действительно оборвалась на лимите.
      setSubsTruncated(items.length >= SUBS_FETCH_LIMIT
        ? Math.max(0, (data.total_count || 0) - items.length)
        : 0);
    } catch {
      setSubs({});
      setSubsTruncated(0);
    }
  }, []);

  useEffect(() => { loadSubs(); }, [loadSubs]);

  // Кто в какой организации (Р12): участник выглядел «Оплатой по факту», хотя
  // расходует общий пакет. Сбой не критичен — отметок просто нет.
  const [orgOf, setOrgOf] = useState({});
  useEffect(() => {
    billingService.adminGetOrgMemberships()
      .then((d) => setOrgOf(Object.fromEntries((d.items || [])
        .map((m) => [(m.email || '').toLowerCase(), m]))))
      .catch(() => setOrgOf({}));
  }, []);

  const handleSearchChange = (value) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(value);
      setPage(1);
    }, 300);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const handleRoleFilter = (value) => {
    setRoleFilter(value);
    setPage(1);
  };

  const handleToggleActive = async (e, user) => {
    e.stopPropagation();
    const prev = user.is_active;
    setToggleError(null);

    // Optimistic update
    setUsers((us) => us.map((u) => u.email === user.email ? { ...u, is_active: !prev } : u));

    try {
      await adminService.updateUser(user.email, { is_active: !prev });
    } catch (err) {
      // Rollback
      setUsers((us) => us.map((u) => u.email === user.email ? { ...u, is_active: prev } : u));
      setToggleError(`Ошибка для ${user.email}: ${err.response?.data?.detail || 'Неизвестная ошибка'}`);
      setTimeout(() => setToggleError(null), 5000);
    }
  };

  const handleDeleteUser = async (e, user) => {
    e.stopPropagation();
    if (!window.confirm(`Удалить пользователя ${user.email}? Все токены будут удалены.`)) return;
    try {
      await adminService.deleteUser(user.email);
      await loadUsers();
    } catch (err) {
      setToggleError(`Ошибка удаления ${user.email}: ${err.response?.data?.detail || 'Неизвестная ошибка'}`);
      setTimeout(() => setToggleError(null), 5000);
    }
  };

  const handleSaveUser = async (email, fields) => {
    await adminService.updateUser(email, fields);
    await loadUsers();
  };

  // Тариф выдаётся внутри карточки отдельным запросом в bit-billing, мимо
  // handleSaveUser. Без перечитывания колонка «Тариф» показывала бы Free до
  // перезагрузки страницы — и админ решил бы, что выдача не сработала.
  const handleCloseEdit = () => {
    setEditUser(null);
    loadSubs();
  };

  const handleCreateToken = async (data) => {
    const result = await adminService.createServiceToken(data);
    return result;
  };

  const handleCloseCreate = (hadToken) => {
    setShowCreateService(false);
    if (hadToken) loadUsers();
  };

  const handleDeactivate = async (email) => {
    await adminService.updateUser(email, { is_active: false });
    await loadOffboarding();
    await loadUsers();
  };

  const handleReactivate = async (email) => {
    await adminService.updateUser(email, { is_active: true });
    await loadOffboarding();
    await loadUsers();
  };

  const handleDismiss = async (email) => {
    await adminService.dismissOffboarding(email);
    await loadOffboarding();
  };

  const candidateCount = Object.values(offState).filter((d) => d.status === 'candidate').length;
  const CHIPS = [
    { key: 'all', label: 'Все' },
    ...(candidateCount ? [{ key: 'candidate', label: `Кандидаты · ${candidateCount}`, warn: true }] : []),
    { key: 'deactivated', label: 'Деактивированные' },
    { key: 'no_office', label: 'Без офиса' },
    { key: 'no_owner', label: 'Без ответственного' },
    { key: 'paying', label: 'Платят' },
  ];

  // Фильтрация и сортировка — на бэке (params.filter / params.sort_by), кроме
  // класса очереди: он фильтруется здесь же, только по строкам текущей страницы.
  const visibleUsers = queueClassFilter
    ? users.filter((u) => (u.queue_class || defaultQueueClass(u.role)) === queueClassFilter)
    : users;

  return (
    <div>
      <div className="flex gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-paper-hint" />
          <Input
            className="pl-9"
            placeholder="Поиск по email..."
            defaultValue={search}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>
        <select
          className="px-3 py-2 rounded-chip bg-paper-bg text-sm min-w-[140px]"
          value={roleFilter}
          onChange={(e) => handleRoleFilter(e.target.value)}
        >
          <option value="">Все роли</option>
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select
          className="px-3 py-2 rounded-chip bg-paper-bg text-sm min-w-[140px]"
          value={queueClassFilter}
          onChange={(e) => setQueueClassFilter(e.target.value)}
          title="Фильтр по классу — только на этой странице, не по всей базе (в отличие от фильтра по роли)"
        >
          <option value="">Все классы</option>
          {QUEUE_CLASSES.map((c) => (
            <option key={c} value={c}>{QUEUE_CLASS_ICONS[c]} {QUEUE_CLASS_LABELS[c]}</option>
          ))}
        </select>
        <div className="flex-1" />
        <Button size="sm" className="bg-paper-accent hover:bg-paper-accent-hover" onClick={() => setShowCreateService(true)}>
          <Plus className="w-4 h-4 mr-1" /> Создать токен
        </Button>
      </div>

      {/* Toggle error toast */}
      {toggleError && (
        <div className="bg-paper-accent/[0.06] text-paper-accent rounded-panel p-3 text-sm">{toggleError}</div>
      )}

      {/* Content */}
      {error ? (
        <div className="bg-paper-accent/[0.06] text-paper-accent rounded-panel p-4">{error}</div>
      ) : loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-paper-accent" />
        </div>
      ) : (
        <>
          <div className="flex gap-2 mb-3">
            {CHIPS.map((c) => (
              <button key={c.key} onClick={() => { setChip(c.key); setPage(1); }}
                className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                  chip === c.key
                    ? (c.warn ? 'bg-paper-warning text-white' : 'bg-paper-ink text-white')
                    : (c.warn ? 'bg-paper-accent/[0.08] text-paper-accent' : 'bg-paper-bg text-paper-ink-2')}`}>
                {c.label}
              </button>
            ))}
          </div>
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-paper-panel border-b text-left">
                    <th className="px-3 py-2.5 font-medium text-paper-ink-3 cursor-pointer" onClick={() => handleSort('email')}>
                      Email <SortArrow field="email" sortBy={sortBy} sortOrder={sortOrder} />
                    </th>
                    <th className="px-3 py-2.5 font-medium text-paper-ink-3">Имя</th>
                    <th className="px-3 py-2.5 font-medium text-paper-ink-3">Роль</th>
                    <th className="px-3 py-2.5 font-medium text-paper-ink-3 text-center" title="Класс очереди GPU">
                      Класс
                    </th>
                    <th className="px-3 py-2.5 font-medium text-paper-ink-3 cursor-pointer whitespace-nowrap"
                        onClick={() => handleSort('office')}
                        title="Контрагент — из справочника, сотрудник — из org-directory. Аккаунты без офиса всегда в конце.">
                      Офис <SortArrow field="office" sortBy={sortBy} sortOrder={sortOrder} />
                    </th>
                    <th className="px-3 py-2.5 font-medium text-paper-ink-3 cursor-pointer whitespace-nowrap"
                        onClick={() => handleSort('owner_email')}
                        title="Сотрудник, отвечающий за учётку контрагента и её ключи.">
                      Ответственный <SortArrow field="owner_email" sortBy={sortBy} sortOrder={sortOrder} />
                    </th>
                    <th className="px-3 py-2.5 font-medium text-paper-ink-3 whitespace-nowrap"
                        title="Тариф клиента и конец оплаченного периода. По истечении клиент молча падает на «Оплату по факту» (100 мин / 3 саммари) — уведомления нет.">
                      Тариф
                    </th>
                    <th className="px-3 py-2.5 font-medium text-paper-ink-3 cursor-pointer" onClick={() => handleSort('created_at')}>
                      Регистрация <SortArrow field="created_at" sortBy={sortBy} sortOrder={sortOrder} />
                    </th>
                    <th className="px-3 py-2.5 font-medium text-paper-ink-3 cursor-pointer" onClick={() => handleSort('last_used')}>
                      Активность <SortArrow field="last_used" sortBy={sortBy} sortOrder={sortOrder} />
                    </th>
                    <th className="px-3 py-2.5 font-medium text-paper-ink-3 text-center">Статус</th>
                    <th className="px-3 py-2.5 font-medium text-paper-ink-3">Оффбординг</th>
                    <th className="px-3 py-2.5 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleUsers.map((user) => (
                    <tr
                      key={user.email}
                      className={`border-b hover:bg-paper-panel cursor-pointer ${!user.is_active ? 'opacity-50' : ''}`}
                      onClick={() => setEditUser(user)}
                    >
                      <td className="px-3 py-2.5">{user.email}</td>
                      <td className="px-3 py-2.5 text-paper-ink-3">
                        {user.display_name
                          ? user.display_name
                          : (() => {
                              const p = userPeople[(user.email || '').toLowerCase()];
                              return p && p.name
                                ? <span className="italic text-paper-hint" title={[p.department, p.job_title].filter(Boolean).join(' · ')}>{p.name}</span>
                                : <span className="italic text-paper-hint">—</span>;
                            })()}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${ROLE_STYLES[user.role] || ROLE_STYLES.user}`}>
                          {user.role}
                        </span>
                        {user.minute_rate != null && (
                          <span className="ml-1 text-xs text-paper-ink-3 whitespace-nowrap">
                            платит · {String(user.minute_rate).replace('.', ',')} ₽
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        {(() => {
                          const cls = user.queue_class || defaultQueueClass(user.role);
                          return <span title={QUEUE_CLASS_LABELS[cls] || cls}>{QUEUE_CLASS_ICONS[cls] || cls}</span>;
                        })()}
                      </td>
                      <td className="px-3 py-2.5 text-paper-ink-3 whitespace-nowrap">
                        {userOffices[(user.email || '').toLowerCase()] || <span className="text-paper-hint">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-paper-ink-3 whitespace-nowrap">
                        {user.owner_email
                          ? (
                            <span className={user.owner_active ? '' : 'text-paper-accent'}>
                              {user.owner_email}{user.owner_active ? '' : ' (уволен)'}
                            </span>
                          )
                          : <span className="text-paper-hint">—</span>}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        {(() => {
                          // Участник (не плательщик) — любой роли: приглашённый
                          // сотрудник остаётся user, а платит за него организация.
                          const m = orgOf[(user.email || '').toLowerCase()];
                          return m && m.role !== 'owner'
                            ? <span data-testid="in-org" className="text-xs text-paper-ink-2">в организации «{m.org_name}»</span>
                            : null;
                        })() || (user.role !== 'client' ? (
                          <span className="text-paper-hint">—</span>
                        ) : (() => {
                          const s = subs[(user.email || '').toLowerCase()];
                          if (!s) return <span className="text-paper-hint text-xs">Оплата по факту</span>;
                          const left = daysLeft(s.paid_until);
                          const tone = expiryTone(s.paid_until);
                          return (
                            <span className="text-xs">
                              <span className="text-paper-ink-2">{s.plan_code}</span>
                              <span className={
                                tone === 'soon' ? ' text-paper-warning' : tone === 'expired' ? ' text-paper-accent' : ' text-paper-hint'
                              }>
                                {' · до '}{formatShortDate(s.paid_until)}
                                {tone === 'soon' && ` (${left} дн.)`}
                              </span>
                            </span>
                          );
                        })())}
                      </td>
                      <td className="px-3 py-2.5 text-paper-ink-3">{formatDate(user.created_at)}</td>
                      <td className="px-3 py-2.5 text-paper-ink-3">{relativeTime(user.last_used)}</td>
                      <td className="px-3 py-2.5 text-center">
                        <button
                          onClick={(e) => handleToggleActive(e, user)}
                          className={`w-9 h-5 rounded-full relative transition-colors ${user.is_active ? 'bg-paper-ink' : 'bg-paper-track'}`}
                        >
                          <span className={`block w-4 h-4 bg-paper-card rounded-full absolute top-0.5 transition-all ${user.is_active ? 'right-0.5' : 'left-0.5'}`} />
                        </button>
                      </td>
                      <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                        {(() => {
                          const st = offState[(user.email || '').toLowerCase()];
                          if (!st) return null;
                          if (st.status === 'candidate') {
                            const d = st.first_flagged_at ? toUtcDate(st.first_flagged_at).toLocaleDateString('ru') : '';
                            return (
                              <span className="inline-flex items-center gap-2">
                                <span className="text-[10px] rounded px-1.5 py-0.5 bg-paper-accent/[0.12] text-paper-accent">кандидат с {d}</span>
                                <button className="text-[10px] text-paper-accent" onClick={() => handleDeactivate(user.email)}>Деактивировать</button>
                                <button className="text-[10px] text-paper-ink-3" onClick={() => handleDismiss(user.email)}>Ложное</button>
                              </span>
                            );
                          }
                          const dd = st.deactivated_at ? toUtcDate(st.deactivated_at).toLocaleDateString('ru') : '';
                          return (
                            <span className="inline-flex items-center gap-2">
                              <span className="text-[10px] rounded px-1.5 py-0.5 bg-paper-accent/[0.12] text-paper-accent">деактив. {dd} авто</span>
                              <button className="text-[10px] text-paper-accent" onClick={() => handleReactivate(user.email)}>Реактивировать</button>
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <button
                          onClick={(e) => handleDeleteUser(e, user)}
                          className="text-paper-hint hover:text-paper-accent transition-colors"
                          title="Удалить пользователя"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {visibleUsers.length === 0 && (
                    <tr><td colSpan={12} className="px-3 py-8 text-center text-paper-hint">
                      {/* users.length > 0 при visibleUsers.length === 0 может дать только
                          клиентский фильтр по классу — «не найдены» здесь было бы неправдой:
                          нужный класс мог просто не попасть на эту серверную страницу. */}
                      {users.length > 0
                        ? 'На этой странице нет — попробуйте другую страницу или сузьте поиск'
                        : 'Пользователи не найдены'}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
          {subsTruncated > 0 && (
            <div className="mt-2 text-xs text-paper-warning bg-paper-panel rounded p-2">
              Колонка «Тариф» построена по первым {SUBS_FETCH_LIMIT} подпискам, ещё {subsTruncated} не
              загружены — у них тариф показан как «Оплата по факту», хотя он есть.
            </div>
          )}
          <Pagination
            page={page}
            totalCount={totalCount}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
            queueClassFilterActive={!!queueClassFilter}
            visibleCount={visibleUsers.length}
            pageUsersCount={users.length}
          />
        </>
      )}

      {/* Dialogs */}
      <EditUserDialog
        user={editUser}
        open={!!editUser}
        onClose={handleCloseEdit}
        onSave={handleSaveUser}
        offices={offices}
      />
      <CreateServiceTokenDialog
        open={showCreateService}
        onClose={handleCloseCreate}
        onCreate={handleCreateToken}
        offices={offices}
      />
    </div>
  );
}
